import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { platformClient } from "@homy/api-client";
import { useSearchParams } from "react-router-dom";
import {
  PageContainer,
  PageHeader,
  Button,
  Input,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  Alert,
  AlertDescription,
} from "@homy/ui";
import {
  Building2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Power,
  UserCheck,
  CheckCircle2,
  AlertTriangle,
  Users,
  Home,
  Calendar,
  Globe,
} from "lucide-react";
import { ImpersonationModal } from "../components/layout/ImpersonationModal";

export function OrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);

  // New Org Form State
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSlug, setNewOrgSlug] = useState("");
  const [newOrgEmail, setNewOrgEmail] = useState("");
  const [newOrgPhone, setNewOrgPhone] = useState("");
  const [newOrgCountry, setNewOrgCountry] = useState("CL");
  const [newOrgType, setNewOrgType] = useState("BROKERAGE");
  const [createError, setCreateError] = useState(null);

  // Check URL query for ?create=true
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setCreateModalOpen(true);
      searchParams.delete("create");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Query: Organizations list
  const {
    data: orgsData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["platform-organizations", searchTerm, statusFilter],
    queryFn: async () => {
      const params = {
        limit: 100,
        skip: 0,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter !== "ALL") params.status = statusFilter;

      const res = await platformClient.get("/organizations", { params });
      return res.data;
    },
  });

  // Query: Organization detail
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ["platform-organization-data", selectedOrgId],
    queryFn: async () => {
      if (!selectedOrgId) return null;
      const res = await platformClient.get(`/organizations/${selectedOrgId}/data`);
      return res.data;
    },
    enabled: Boolean(selectedOrgId && detailModalOpen),
  });

  // Mutation: Create Organization
  const createOrgMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await platformClient.post("/organizations", payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["platform-metrics"] });
      setCreateModalOpen(false);
      resetCreateForm();
    },
    onError: (err) => {
      setCreateError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to provision organization"
      );
    },
  });

  // Mutation: Toggle Status (Suspend / Reactivate)
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }) => {
      const res = await platformClient.put(`/organizations/${id}/status`, {
        status: newStatus,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["platform-metrics"] });
      if (selectedOrgId) {
        queryClient.invalidateQueries({
          queryKey: ["platform-organization-data", selectedOrgId],
        });
      }
    },
  });

  const resetCreateForm = () => {
    setNewOrgName("");
    setNewOrgSlug("");
    setNewOrgEmail("");
    setNewOrgPhone("");
    setNewOrgCountry("CL");
    setNewOrgType("BROKERAGE");
    setCreateError(null);
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      setCreateError("Organization name is required");
      return;
    }
    if (!newOrgEmail.trim()) {
      setCreateError("Contact email is required");
      return;
    }

    createOrgMutation.mutate({
      name: newOrgName.trim(),
      slug: newOrgSlug.trim() || undefined,
      contact_email: newOrgEmail.trim(),
      contact_phone: newOrgPhone.trim() || undefined,
      country_code: newOrgCountry,
      type: newOrgType,
    });
  };

  const handleToggleStatus = (org) => {
    const nextStatus = org.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    toggleStatusMutation.mutate({ id: org.id, newStatus: nextStatus });
  };

  const items = orgsData?.items || [];

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Tenant Organizations"
        description="Comprehensive directory of multi-tenant brokerages, property managers, and institutional landlords."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => {
                resetCreateForm();
                setCreateModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Provision Organization
            </Button>
          </div>
        }
      />

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-card border border-border p-3 rounded-lg">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search organizations by name, slug, or contact email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-input"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            Loading tenant organizations...
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground space-y-2">
            <Building2 className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p>No organizations found matching your search criteria.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[280px]">Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((org) => {
                const isActive = org.status === "ACTIVE";
                return (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div>
                        <div className="font-semibold text-foreground flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span>{org.name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono pl-6">
                          {org.slug || org.id}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          isActive
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                            : "border-amber-500/30 bg-amber-500/10 text-amber-400"
                        }
                      >
                        {isActive ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> ACTIVE
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> SUSPENDED
                          </span>
                        )}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {org.tier || "PRO"}
                      </Badge>
                    </TableCell>

                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {org.countryCode || "CL"}
                    </TableCell>

                    <TableCell className="text-right font-medium text-xs">
                      {org.userCount ?? 0}
                    </TableCell>

                    <TableCell className="text-right font-medium text-xs">
                      {org.propertyCount ?? 0}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground">
                      {org.createdAt
                        ? new Date(org.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="View metadata details"
                          onClick={() => {
                            setSelectedOrgId(org.id);
                            setDetailModalOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 ${
                            isActive
                              ? "text-muted-foreground hover:text-amber-400"
                              : "text-amber-400 hover:text-emerald-400"
                          }`}
                          title={isActive ? "Suspend organization" : "Reactivate organization"}
                          onClick={() => handleToggleStatus(org)}
                          disabled={toggleStatusMutation.isPending}
                        >
                          <Power className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                          title="Launch diagnostic impersonation for tenant"
                          onClick={() => {
                            setImpersonateTarget({ orgId: org.id });
                          }}
                        >
                          <UserCheck className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Organization Creation Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 text-primary">
              <Building2 className="h-5 w-5" />
              <DialogTitle>Provision Tenant Organization</DialogTitle>
            </div>
            <DialogDescription>
              Create a new tenant organization in the Homy platform. A dedicated tenant domain and
              workspace will be instantiated.
            </DialogDescription>
          </DialogHeader>

          {createError && (
            <Alert variant="destructive" className="my-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleCreateSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Organization / Brokerage Name *</Label>
              <Input
                id="org-name"
                placeholder="e.g. Apex Property Management"
                value={newOrgName}
                onChange={(e) => {
                  setNewOrgName(e.target.value);
                  if (!newOrgSlug) {
                    setNewOrgSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, "")
                    );
                  }
                }}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-slug">Slug (Identifier)</Label>
              <Input
                id="org-slug"
                placeholder="e.g. apex-property-mgmt"
                value={newOrgSlug}
                onChange={(e) => setNewOrgSlug(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-email">Administrative Contact Email *</Label>
              <Input
                id="org-email"
                type="email"
                placeholder="admin@apexproperties.com"
                value={newOrgEmail}
                onChange={(e) => setNewOrgEmail(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="org-country">Country Code</Label>
                <select
                  id="org-country"
                  value={newOrgCountry}
                  onChange={(e) => setNewOrgCountry(e.target.value)}
                  className="h-10 w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="CL">Chile (CL)</option>
                  <option value="AR">Argentina (AR)</option>
                  <option value="MX">Mexico (MX)</option>
                  <option value="CO">Colombia (CO)</option>
                  <option value="US">United States (US)</option>
                  <option value="ES">Spain (ES)</option>
                  <option value="BR">Brazil (BR)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="org-type">Organization Type</Label>
                <select
                  id="org-type"
                  value={newOrgType}
                  onChange={(e) => setNewOrgType(e.target.value)}
                  className="h-10 w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="BROKERAGE">Brokerage</option>
                  <option value="PROPERTY_MANAGER">Property Manager</option>
                  <option value="LANDLORD">Landlord Portfolio</option>
                  <option value="ENTERPRISE">Enterprise Real Estate</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-phone">Contact Phone</Label>
              <Input
                id="org-phone"
                placeholder="+56 9 1234 5678"
                value={newOrgPhone}
                onChange={(e) => setNewOrgPhone(e.target.value)}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createOrgMutation.isPending}>
                {createOrgMutation.isPending
                  ? "Provisioning..."
                  : "Provision Organization"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Organization Detail Drawer / Dialog */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-center gap-2 text-primary">
              <Building2 className="h-5 w-5" />
              <DialogTitle>Organization Metadata & Profile</DialogTitle>
            </div>
            <DialogDescription>
              Detailed configuration, membership, and telemetry inspection for the selected tenant.
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Loading tenant metadata...
            </div>
          ) : detailData?.organization ? (
            <div className="space-y-4 py-2 text-xs">
              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    {detailData.organization.name}
                  </h3>
                  <Badge
                    variant="outline"
                    className={
                      detailData.organization.status === "ACTIVE"
                        ? "border-emerald-500/30 text-emerald-400"
                        : "border-amber-500/30 text-amber-400"
                    }
                  >
                    {detailData.organization.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-muted-foreground pt-1">
                  <div>
                    <span className="text-[11px] block">Organization ID</span>
                    <span className="font-mono text-foreground text-[11px]">
                      {detailData.organization.id}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] block">Slug</span>
                    <span className="font-mono text-foreground text-[11px]">
                      {detailData.organization.slug || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] block">Country</span>
                    <span className="text-foreground flex items-center gap-1">
                      <Globe className="h-3 w-3" /> {detailData.organization.countryCode || "CL"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] block">Type</span>
                    <span className="text-foreground font-mono">
                      {detailData.organization.type || "BROKERAGE"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Counts Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border bg-card p-3 flex items-center gap-3">
                  <div className="p-2 rounded-md bg-primary/10 text-primary">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-muted-foreground text-[11px]">Active Members</div>
                    <div className="text-base font-semibold text-foreground">
                      {detailData.usersCount ?? 0}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-card p-3 flex items-center gap-3">
                  <div className="p-2 rounded-md bg-emerald-500/10 text-emerald-400">
                    <Home className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-muted-foreground text-[11px]">Managed Units</div>
                    <div className="text-base font-semibold text-foreground">
                      {detailData.propertiesCount ?? 0}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-muted-foreground pt-2 border-t border-border">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Created: {detailData.organization.createdAt ? new Date(detailData.organization.createdAt).toLocaleString() : "—"}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleToggleStatus(detailData.organization);
                  }}
                  disabled={toggleStatusMutation.isPending}
                  className="gap-1 text-xs"
                >
                  <Power className="h-3.5 w-3.5" />
                  {detailData.organization.status === "ACTIVE"
                    ? "Suspend Organization"
                    : "Reactivate Organization"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-destructive">
              Unable to load organization details.
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImpersonationModal
        open={Boolean(impersonateTarget)}
        onOpenChange={(open) => {
          if (!open) setImpersonateTarget(null);
        }}
        initialOrgId={impersonateTarget?.orgId || ""}
      />
    </PageContainer>
  );
}
